import React from 'react';
import { Box, Typography, Chip, Stack, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimerIcon from '@mui/icons-material/Timer';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { SchedulingConfigurationDTO } from '@aphp-ema/common/scheduling/schedulingConfiguration.dto';

type EmaQuestionnaireSchedulingConfigurationProps = {
  configuration: SchedulingConfigurationDTO;
};

export const EmaQuestionnaireSchedulingConfiguration: React.FC<
  EmaQuestionnaireSchedulingConfigurationProps
> = ({ configuration }) => {
  const { t } = useTranslation();
  const hasReminders =
    configuration.reminderCount && configuration.reminderCount > 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
        <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight="600" color="text.primary">
          {t('pages.studies.emaQuestionnaire.scheduling.configuration.title')}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} mb={hasReminders ? 2 : 0}>
        <Chip
          icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
          label={t(
            'pages.studies.emaQuestionnaire.scheduling.configuration.numberOfDays',
            { count: configuration.numberOfDays },
          )}
          variant="outlined"
          size="small"
          color="primary"
          sx={{
            fontWeight: 500,
            borderRadius: 1.5,
          }}
        />
        <Chip
          icon={<TimerIcon sx={{ fontSize: 16 }} />}
          label={t(
            'pages.studies.emaQuestionnaire.scheduling.configuration.validityInMinutes',
            { count: configuration.validityInMinutes },
          )}
          variant="outlined"
          size="small"
          color="primary"
          sx={{
            fontWeight: 500,
            borderRadius: 1.5,
          }}
        />
      </Stack>

      {hasReminders && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <NotificationsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography
              variant="subtitle2"
              fontWeight="600"
              color="text.primary"
            >
              {t(
                'pages.studies.emaQuestionnaire.scheduling.configuration.reminders.title',
              )}
            </Typography>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <Chip
              icon={<NotificationsIcon sx={{ fontSize: 16 }} />}
              label={t(
                'pages.studies.emaQuestionnaire.scheduling.configuration.reminders.count',
                { count: configuration.reminderCount },
              )}
              variant="outlined"
              size="small"
              color="secondary"
              sx={{
                fontWeight: 500,
                borderRadius: 1.5,
              }}
            />
            {configuration.reminderDelayMinutes && (
              <Chip
                icon={<TimerIcon sx={{ fontSize: 16 }} />}
                label={t(
                  'pages.studies.emaQuestionnaire.scheduling.configuration.reminders.delay',
                  { count: configuration.reminderDelayMinutes },
                )}
                variant="outlined"
                size="small"
                color="secondary"
                sx={{
                  fontWeight: 500,
                  borderRadius: 1.5,
                }}
              />
            )}
            {configuration.reminderText && (
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  ml: 1,
                }}
              >
                {t(
                  'pages.studies.emaQuestionnaire.scheduling.configuration.reminders.text',
                  { text: configuration.reminderText },
                )}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};
